import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { ChevronDown, X } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "clothing", label: "Clothing" },
  { key: "hijabs", label: "Hijabs" },
  { key: "bags", label: "Bags" },
  { key: "accessories", label: "Accessories" },
];

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "popular", label: "Most popular" },
  { key: "price_asc", label: "Price · Low to high" },
  { key: "price_desc", label: "Price · High to low" },
];

export default function Shop() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSort = searchParams.get("sort") || "newest";
  const initialQ = searchParams.get("q") || "";

  const [cat, setCat] = useState(category || "all");
  const [sort, setSort] = useState(initialSort);
  const [q, setQ] = useState(initialQ);
  const [priceMax, setPriceMax] = useState(800);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setCat(category || "all"); }, [category]);

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (cat && cat !== "all") params.category = cat;
    if (q) params.search = q;
    if (priceMax < 800) params.max_price = priceMax;
    api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [cat, sort, q, priceMax]);

  const heroTitle = useMemo(() => {
    if (cat === "clothing") return "Women's Clothing";
    if (cat === "hijabs") return "Hijab Collection";
    if (cat === "bags") return "Bags";
    if (cat === "accessories") return "Accessories";
    return "The Shop";
  }, [cat]);

  return (
    <div data-testid="shop-page">
      {/* Page header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-10">
        <div className="overline">Shop</div>
        <h1 className="serif text-5xl lg:text-7xl mt-3">{heroTitle}</h1>
        <p className="text-[#5A5A5A] mt-3 max-w-xl">{products.length} pieces · curated, never excessive.</p>
      </section>

      {/* Filter bar */}
      <section className="border-y border-[#E5E5DF] sticky top-[120px] lg:top-[155px] bg-[#F5F5F0]/95 backdrop-blur z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex gap-2 lg:gap-6 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((c) => (
              <Link
                key={c.key}
                to={c.key === "all" ? "/shop" : `/shop/${c.key}`}
                data-testid={`filter-cat-${c.key}`}
                className={`text-[0.7rem] tracking-[0.25em] uppercase whitespace-nowrap pb-1 border-b transition-colors ${cat === c.key ? "border-[#C5A059] text-[#0A0A0A]" : "border-transparent text-[#5A5A5A] hover:text-[#0A0A0A]"}`}
              >
                {c.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex items-center gap-2 text-xs">
              <span className="text-[#5A5A5A]">Under AED {priceMax}</span>
              <input type="range" min="50" max="800" step="25" value={priceMax} onChange={(e) => setPriceMax(+e.target.value)} className="accent-[#C5A059]" data-testid="filter-price-range" />
            </div>
            <div className="relative">
              <select
                data-testid="sort-select"
                value={sort} onChange={(e) => setSort(e.target.value)}
                className="appearance-none bg-transparent border border-[#E5E5DF] pl-3 pr-8 py-2 text-xs uppercase tracking-[0.2em] cursor-pointer"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Search active pill */}
      {q && (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-6">
          <span className="inline-flex items-center gap-2 text-xs border border-[#0A0A0A] px-3 py-1.5">
            Search: <strong>{q}</strong>
            <button onClick={() => { setQ(""); setSearchParams({}); }} data-testid="clear-search"><X size={12} /></button>
          </span>
        </div>
      )}

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
        {loading ? (
          <div className="text-center py-32 overline">Loading the edit…</div>
        ) : products.length === 0 ? (
          <div className="text-center py-32">
            <div className="overline">No matches</div>
            <p className="serif text-2xl mt-4">Try a different category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12" data-testid="shop-grid">
            {products.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
